import React, { useState, useContext, useEffect } from 'react';
import { NavBar, Tabs, List, Ellipsis, Skeleton, ErrorBlock, Badge, Button, Dialog, Toast } from 'antd-mobile';
import { ExclamationCircleOutline } from 'antd-mobile-icons';
import get from 'lodash/get';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { deleteWorkOrderDefect, getDefect, getDefectStats, updateWorkOrderDefect } from '../api';
import Signatures from '../components/Signatures';
import InputComponent from '../components/Input';
import Materials from './Materials';
import Files from './Files';
import Notes from './Notes';
import { featureEnabled, matchesPermission } from '../utils/permission-helpers';
import Tools from './Tools';
import WorkTimeToggle from '../components/WorkTimeToggle';
import UserSelfContext from '../utils/self-context';
import { OBJECT_TYPE_ADDITIONAL_WORK } from '../utils/constants';
import CustomFieldInputItem from '../components/CustomFieldsInput';
import SubTasks from './SubTasks';

const DefectWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;
const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow:  auto;
	width: 100%;
	background: #f0f2f5;
`;

const ToastContent = styled.div`
	word-break: normal;
	text-align: center;
	width: 100%;
`;

const ButtonWrapper = styled.div`
	padding: 0px 8px 8px 8px;
	margin-top: 12px;
`;

const Overview = styled.div`
	padding: 12px 0px;
`;

const DefectInfo = styled.div`
	padding: 0px 12px;
	background: #fff;

	.adm-list-item-title {
		margin-bottom: 8px;
		display: flex;
		align-items: center;
	}

	h2 {
		margin: 0px 8px 0px 0px;
		display: inline-block;
		color: var(--adm-color-text);
	}

	.adm-ellipsis {
		a {
			margin-left: 8px;
		}
	}
`;

const NotFound = styled(ErrorBlock)`
	height: 100%;
	padding: 12px;
	justify-content: center;
	align-items: center;
	display: flex;
	flex-direction: column;
`;

const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const Description = styled(List.Item)`
	font-size: var(--adm-font-size-9);
`;

const TitleWrapper = styled.div`
	display: flex;
	align-items: center;
	width: calc(100vw - 24px);
`;

const TitleActions = styled.div`
	margin-left: auto;
	display: flex;
	align-items: center;
`;

const TabBar = styled(Tabs)`
	display: flex;
	flex-direction: column;
	height: 100%;
	.adm-tabs-header {
		background: #fff;
	}

	.adm-tabs-content {
		flex: 1;
		overflow: auto;
		padding: 0;
	}
`;

function Defect() {
	const params = useParams();
	const history = useHistory();
	const { id, workOrderId } = params;
	const { userSelf } = useContext(UserSelfContext);
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState(false);
	const [saving, setSaving] = useState(false);
	const [defect, setDefect] = useState({});
	const [stats, setStats] = useState({});

	async function fetchDefect(silent) {
		if (!silent) {
			setLoading(true);
		}

		try {
			const data = await getDefect(workOrderId, id);
			setDefect(data);
		} catch (e) {
			setErr(true);
		}

		setLoading(false);
	}

	async function fetchDefectStats() {
		setLoading(true);
		try {
			const data = await getDefectStats(workOrderId, id);
			setStats(data);
		} catch (e) {
			setErr(true);
		}

		setLoading(false);
	}

	useEffect(() => {
		fetchDefect();
		fetchDefectStats();
	}, []);

	async function updateDefect(data) {
		const payload = data;
		payload.id = defect.id;

		try {
			const respData = await updateWorkOrderDefect(workOrderId, data);
			setDefect(respData);
		} catch (e) {
			setErr(true);
		}
	}

	async function handleDelete(id) {
		const result = await Dialog.confirm({
			cancelText: 'Cancel',
			confirmText: 'Yes',
			style: { textAlign: 'center' },
			content: 'Delete this Additional Work?'
		});

		if (result) {
			setSaving(true);
			try {
				await deleteWorkOrderDefect(defect.work_order_id, defect.id);
				history.push(`/work-order/${defect.work_order_id}`);
			} catch (e) {
				Toast.show({
					icon: 'fail',
					content: <ToastContent>
						Unable to delete Additional Work. Something went wrong.
					</ToastContent>
				});
			}

			setSaving(false);
		}
	}

	function getRemainingTasks() {
		return defect.has_signable_tasks ? Number(get(stats, 'total_tasks') - get(stats, 'done_tasks')) || 0 : 0;
	}


	return (err ? <NotFound
		title="Additional work not found"
		description="This additional work does not exist, or you dont have rights to view it"
		status='empty'
	/> : <DefectWrapper>
		<Nav onBack={() => history.push(`/work-order/${defect.work_order_id}`)} back={''}>
			Additional work
		</Nav>
		<DefectInfo>
			{loading ? <Skeleton.Paragraph lineCount={2} animated/> :
				<Description
					title={<TitleWrapper>
						<TitleActions>
							<WorkTimeToggle parent={defect} parentType="defect" />
						</TitleActions>
					</TitleWrapper>}
				>
					<Ellipsis
						direction='end'
						rows={4} content={defect.description || '-'}
						expandText="Show"
						collapseText="Hide"
					/>
				</Description>
			}
		</DefectInfo>
		<ContentWrapper>
			<TabBar>
				<Tabs.Tab
					title={'Overview'}
					key='overview'
				>
					<Overview>
						<InputComponent
							placeholder="Insert rectification method"
							onSave={updateDefect}
							disabled={!matchesPermission(userSelf, 'edit_defects') || defect.signatures?.length > 0}
							dataKey="restification_method"
							isRequired={true}
							initialValue={defect.restification_method}
							title="Rectification method"
							type="text"
						/>
						<InputComponent
							placeholder="Insert reference"
							onSave={updateDefect}
							disabled={!matchesPermission(userSelf, 'edit_defects')}
							dataKey="reference"
							requiredRulesObjectType={OBJECT_TYPE_ADDITIONAL_WORK}
							initialValue={defect.reference}
							title="Reference"
							type="text"
						/>
						<InputComponent
							placeholder="ATA"
							onSave={updateDefect}
							dataKey="ata"
							requiredRulesObjectType={OBJECT_TYPE_ADDITIONAL_WORK}
							disabled={!matchesPermission(userSelf, 'edit_defects')}
							initialValue={defect.ata}
							title="ATA"
						/>
						<InputComponent
							placeholder="Category"
							onSave={updateDefect}
							dataKey="category"
							requiredRulesObjectType={OBJECT_TYPE_ADDITIONAL_WORK}
							disabled={!matchesPermission(userSelf, 'edit_defects')}
							initialValue={[defect.category]}
							title="Category"
							type="select"
							options={[
								...(get(userSelf, 'settings.maintenance_item_categories') || []).map(t => ({ label: t, value: t }))
							]}
						/>
						{
							featureEnabled(userSelf, 'access_panels_enabled') &&
							<List header="Access panels">
								<List.Item>
									{defect.access_panels?.join(', ') || '-'}
								</List.Item>
							</List>
						}
						<InputComponent
							placeholder="Critical task"
							onSave={updateDefect}
							requiredRulesObjectType={OBJECT_TYPE_ADDITIONAL_WORK}
							dataKey="critical"
							type="switch"
							getCustomDisplayValue={val => (val ? <span style={{ color: '#ff411c', fontWeight: 'bold' }}><ExclamationCircleOutline /> Yes</span> : <span>No</span>)}
							disabled={!matchesPermission(userSelf, 'edit_defects')}
							initialValue={defect.critical}
							title="Critical task"
						/>
						{(defect.saved_custom_fields || [])
							.map(field => (
								<CustomFieldInputItem
									key={field.key}
									model={defect}
									field={{ ...field, object_type: OBJECT_TYPE_ADDITIONAL_WORK }}
									onSave={updateDefect}
									disabled={!matchesPermission(userSelf, 'edit_defects')}
								/>
							))}
						<InputComponent
							placeholder="Type comment"
							disabled={!matchesPermission(userSelf, 'edit_defects')}
							onSave={updateDefect}
							dataKey="comment"
							initialValue={defect.comment}
							title="Comment"
							type="text"
						/>
						<InputComponent
							onSave={updateDefect}
							disabled={!matchesPermission(userSelf, 'edit_defects')}
							dataKey="done_date"
							isRequired={true}
							title="Done date"
							type="date"
							initialValue={defect.done_date}
						/>
						{defect.work_order_op_id && <List header="Related Task">
							<List.Item
								key={defect.id}
								onClick={() => history.push(`/work-order/${defect.work_order_id}/task/${defect.work_order_op_id}`)}
								clickable
							>
								<Ellipsis direction='end' rows={3} content={defect.work_order_op_name || '-'} />
							</List.Item>
						</List>}
						{featureEnabled(userSelf, 'work_order_staff_enabled') && <Signatures
							onChange={async (id, removed) => {
								if (!removed) {
									await updateDefect({
										done_date: moment()
									});
								}

								fetchDefect(true);
							}}
							item={defect}
							objectType={'defect'}
						/>}
						{matchesPermission(userSelf, 'delete_defects') && <ButtonWrapper>
							<Button
								block
								saving={saving}
								onClick={() => handleDelete(defect.id)}
								color="danger"
								fill="outline"
							>
								Delete
							</Button>
						</ButtonWrapper>}
					</Overview>
				</Tabs.Tab>
				{defect.has_tasks && <Tabs.Tab
					title={<Badge content={getRemainingTasks() || null} style={{ '--right': '-10px', '--top': '4px' }}>Subtasks</Badge>}
					key='subtasks'
				>
					<SubTasks
						onTasksChange={() => {
							fetchDefectStats();
							fetchDefect();
						}}
						defectId={defect.id}
					/>
				</Tabs.Tab>}
				{featureEnabled(userSelf, 'warehouse_enabled') && matchesPermission(userSelf, 'view_work_order_materials') &&
					<Tabs.Tab title='Materials' key='materials'>
						<Materials defectId={defect.id} workOrderId={defect.work_order_id} />
					</Tabs.Tab>
				}
				{featureEnabled(userSelf, 'tools_enabled') && matchesPermission(userSelf, 'view_work_order_tools') &&
					<Tabs.Tab title='Tools' key='tools'>
						<Tools defectId={defect.id} workOrderId={defect.work_order_id} />
					</Tabs.Tab>
				}
				<Tabs.Tab
					title={<Badge
						content={defect.files_count || null}
						style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
							Files
					</Badge>}
					key='files'
				>
					<Files
						onChange={() => fetchDefect(true)}
						objectType="defect"
						disabled={!matchesPermission(userSelf, 'manage_files_notes_defects')}
						objectId={defect.id}
					/>
				</Tabs.Tab>
				<Tabs.Tab
					title={<Badge
						content={defect.notes_count || null}
						style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
							Notes
					</Badge>}
					key='notes'
				>
					<Notes
						onChange={() => fetchDefect(true)}
						objectType="defect"
						disabled={!matchesPermission(userSelf, 'manage_files_notes_defects')}
						objectId={defect.id}
					/>
				</Tabs.Tab>
			</TabBar>
		</ContentWrapper>
	</DefectWrapper>);
}

export default Defect;
